// src/components/BookSession.js
import { isVipCustomer } from "../utils/customerStatus";
import React, { useState, useEffect, useRef } from "react";
import {
  getMembershipFriendlyName,
  getPackageFriendlyName,
} from "../utils/membershipUtils";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  bookingsAPI,
  pricingAPI,
  profileAPI,
  suitesAPI,
} from "../services/api";
import {
  getMyWaiverStatus,
  hasFullySignedWaivers,
  WAIVER_GATE_PATH,
} from "../services/waiverApi";
import { handleApiError } from "../utils/errorUtils";
import { isPhoenixTime } from "../utils/bookingReturnUtils";
import {
  getBookingOptionLabel,
  getSessionDisplayName,
  getSessionServiceName,
  getSessionSuiteLabel,
  isMassageSessionType,
} from "../utils/sessionTypeUtils";
import "react-datepicker/dist/react-datepicker.css";
import "./BookSession.css";

const ARIZONA_PIMA_SALES_TAX_RATE = 0.086;
const centsToDollars = (cents) => Number((Number(cents) / 100).toFixed(2));
// Display text for a /api/pricing item (amount before tax), or null while
// prices load. Whole dollars drop the cents ("$30"), other amounts keep
// them ("$12.50").
const formatPriceItem = (item) => {
  if (!item) return null;
  const dollars = centsToDollars(item.amountCents);
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};
// Massage price for display from /api/pricing (read from Stripe), or null
// while prices load. The backend charges the same Stripe price.
const getMassagePriceDisplay = (pricing, sessionType) => {
  const item = pricing?.massages?.[sessionType];
  if (!item) return null;
  return {
    price: centsToDollars(item.amountCents),
    tax: centsToDollars(item.taxCents),
    total: centsToDollars(item.totalCents),
  };
};

// CORP-DOUBLE-OCC-002 (Scenario 4 — Q7 LOCKED).
// Pragmatic RFC-5322-ish email regex; matches the patterns the deployed
// backend accepts for the existing $25 guest path. Intentionally NOT a
// full RFC parse — over-strict regex blocks legitimate addresses.
const CORP_DOUBLE_OCC_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Post-purchase auto-submit: the purchased credit lands when the Stripe
// webhook is processed, which can be after the customer is back on this
// page. Retry the submit while the credit is missing.
const AUTO_SUBMIT_MAX_RETRIES = 5;
const AUTO_SUBMIT_RETRY_MS = 2000;
// US-phone-format-tolerant regex per Zuri spec — accepts `(555) 555-5555`,
// `555-555-5555`, `5555555555`, `+1 555 555 5555` and similar variants.
// Counts digits after stripping non-digits; 10 (with optional leading 1)
// is the canonical accepted shape.
const isValidCorpGuestPhone = (raw) => {
  if (typeof raw !== "string") return false;
  const digits = raw.replace(/[^\d]/g, "");
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits[0] === "1") return true;
  return false;
};
const isValidCorpGuestEmail = (raw) =>
  typeof raw === "string" && CORP_DOUBLE_OCC_EMAIL_PATTERN.test(raw.trim());
const isCorpGuestFormValid = (corpGuestInfo) =>
  Boolean(
    corpGuestInfo &&
      typeof corpGuestInfo.name === "string" &&
      corpGuestInfo.name.trim().length > 0 &&
      isValidCorpGuestEmail(corpGuestInfo.email) &&
      isValidCorpGuestPhone(corpGuestInfo.phone)
  );


const isWednesdayInStudioTime = (date) =>
  moment(date).tz("America/Phoenix").day() === 3;

const getPersonalCreditSource = (profileData, sessionType, referenceDate) => {
  if (!profileData) {
    return null;
  }

  if (sessionType === "infrared") {
    if (profileData.giftedSaunaCredits?.remaining > 0) {
      return { type: "gifted", description: "Gifted Credits" };
    }
    if (profileData.sharedGiftedCredits?.remaining > 0) {
      return { type: "gifted", description: "Flexible Gifted Credits" };
    }
    if (
      profileData.wellnessWednesdayCredits?.remaining > 0 &&
      isWednesdayInStudioTime(referenceDate)
    ) {
      return {
        type: "promotion",
        description: "Wellness Wednesday Session",
      };
    }
    if (profileData.dropInSaunaCount?.remaining > 0) {
      return { type: "drop-in", description: "Drop-in Credits" };
    }
    if (profileData.sharedDropInCount?.remaining > 0) {
      return { type: "drop-in", description: "Flexible Drop-In Credits" };
    }
    if (
      profileData.saunaPackageName &&
      profileData.saunaPackageName !== "none" &&
      profileData.saunaPackageSessionUsage?.remaining > 0
    ) {
      return {
        type: "package",
        description: `Package: ${getPackageFriendlyName(profileData.saunaPackageName)}`,
      };
    }
    if (
      profileData.sharedPackageName &&
      profileData.sharedPackageName !== "none" &&
      profileData.sharedPackageSessionUsage?.remaining > 0
    ) {
      return {
        type: "package",
        description: `Package: ${profileData.sharedPackageName}`,
      };
    }
    if (
      profileData.creditSystemType === "unified_shared" &&
      profileData.sharedMembershipSessionUsage?.remaining > 0
    ) {
      return {
        type: "membership",
        description: "Membership: Flexible Session Credits",
      };
    }
    if (
      profileData.saunaMembershipType &&
      profileData.saunaMembershipType !== "none" &&
      profileData.saunaMembershipSessionUsage?.remaining > 0
    ) {
      return {
        type: "membership",
        description: `Membership: ${getMembershipFriendlyName(profileData.saunaMembershipType)}`,
      };
    }
  }

  if (sessionType === "redlight") {
    if (profileData.giftedRedLightCredits?.remaining > 0) {
      return { type: "gifted", description: "Gifted Credits" };
    }
    if (profileData.sharedGiftedCredits?.remaining > 0) {
      return { type: "gifted", description: "Flexible Gifted Credits" };
    }
    if (
      profileData.wellnessWednesdayCredits?.remaining > 0 &&
      isWednesdayInStudioTime(referenceDate)
    ) {
      return {
        type: "promotion",
        description: "Wellness Wednesday Session",
      };
    }
    if (profileData.dropInRedLightCount?.remaining > 0) {
      return { type: "drop-in", description: "Drop-in Credits" };
    }
    if (profileData.sharedDropInCount?.remaining > 0) {
      return { type: "drop-in", description: "Flexible Drop-In Credits" };
    }
    if (
      profileData.redLightPackageName &&
      profileData.redLightPackageName !== "none" &&
      profileData.redLightPackageSessionUsage?.remaining > 0
    ) {
      return {
        type: "package",
        description: `Package: ${getPackageFriendlyName(profileData.redLightPackageName)}`,
      };
    }
    if (
      profileData.sharedPackageName &&
      profileData.sharedPackageName !== "none" &&
      profileData.sharedPackageSessionUsage?.remaining > 0
    ) {
      return {
        type: "package",
        description: `Package: ${profileData.sharedPackageName}`,
      };
    }
    if (
      profileData.creditSystemType === "unified_shared" &&
      profileData.sharedMembershipSessionUsage?.remaining > 0
    ) {
      return {
        type: "membership",
        description: "Membership: Flexible Session Credits",
      };
    }
    if (
      profileData.redLightMembershipType &&
      profileData.redLightMembershipType !== "none" &&
      profileData.redLightMembershipSessionUsage?.remaining > 0
    ) {
      return {
        type: "membership",
        description: `Membership: ${getMembershipFriendlyName(profileData.redLightMembershipType)}`,
      };
    }
  }

  return null;
};

const getDropInPurchasePath = () => "/drop-in-sessions";

const formatPhoenixDate = (value) =>
  new Date(value).toLocaleDateString("en-US", {
    timeZone: "America/Phoenix",
  });

// Strip time-of-day so date-only comparisons against the renewal cutoff behave
// consistently across timezones. renewalCutoff values come from the backend as
// date-only strings or ISO timestamps at 00:00 Phoenix time.
const isDateStrictlyAfter = (candidate, reference) => {
  if (!candidate || !reference) return false;
  const c = new Date(candidate);
  const r = new Date(reference);
  const cDay = Date.UTC(c.getFullYear(), c.getMonth(), c.getDate());
  const rDay = Date.UTC(r.getFullYear(), r.getMonth(), r.getDate());
  return cDay > rDay;
};

const hasNonMembershipCreditsForSession = (profile, sessionType) => {
  if (!profile) return false;
  if (sessionType === "infrared") {
    if ((profile.dropInSaunaCount?.remaining || 0) > 0) return true;
    if ((profile.sharedDropInCount?.remaining || 0) > 0) return true;
    if (
      profile.saunaPackageName &&
      profile.saunaPackageName !== "none" &&
      (profile.saunaPackageSessionUsage?.remaining || 0) > 0
    ) {
      return true;
    }
    if (
      profile.sharedPackageName &&
      profile.sharedPackageName !== "none" &&
      (profile.sharedPackageSessionUsage?.remaining || 0) > 0
    ) {
      return true;
    }
  }
  if (sessionType === "redlight") {
    if ((profile.dropInRedLightCount?.remaining || 0) > 0) return true;
    if ((profile.sharedDropInCount?.remaining || 0) > 0) return true;
    if (
      profile.redLightPackageName &&
      profile.redLightPackageName !== "none" &&
      (profile.redLightPackageSessionUsage?.remaining || 0) > 0
    ) {
      return true;
    }
    if (
      profile.sharedPackageName &&
      profile.sharedPackageName !== "none" &&
      (profile.sharedPackageSessionUsage?.remaining || 0) > 0
    ) {
      return true;
    }
  }
  return false;
};

const countNonMembershipCreditsForSession = (profile, sessionType) => {
  if (!profile) return 0;
  let total = 0;
  if (sessionType === "infrared") {
    total += profile.dropInSaunaCount?.remaining || 0;
    total += profile.sharedDropInCount?.remaining || 0;
    if (profile.saunaPackageName && profile.saunaPackageName !== "none") {
      total += profile.saunaPackageSessionUsage?.remaining || 0;
    }
    if (profile.sharedPackageName && profile.sharedPackageName !== "none") {
      total += profile.sharedPackageSessionUsage?.remaining || 0;
    }
  }
  if (sessionType === "redlight") {
    total += profile.dropInRedLightCount?.remaining || 0;
    total += profile.sharedDropInCount?.remaining || 0;
    if (profile.redLightPackageName && profile.redLightPackageName !== "none") {
      total += profile.redLightPackageSessionUsage?.remaining || 0;
    }
    if (profile.sharedPackageName && profile.sharedPackageName !== "none") {
      total += profile.sharedPackageSessionUsage?.remaining || 0;
    }
  }
  return total;
};

const toIsoDateOnly = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const BookSession = ({ onBookingSuccess = null, onCancel = null }) => {
  const [sessionType, setSessionType] = useState("redlight"); // default redlight
  const [selectedDay, setSelectedDay] = useState(new Date()); // Use current date as default
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableSuites, setAvailableSuites] = useState([]);
  const [filteredSuites, setFilteredSuites] = useState([]);
  const [selectedSuite, setSelectedSuite] = useState("");
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [addGuest, setAddGuest] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: "",
    email: "",
    phone: "",
  });
  // IMANI-BASELINE-HYGIENE-001 (Sprint 15): the aromatherapy upsell was
  // de-scoped from the booking flow but the field is still sent to the BE
  // in the POST body (line ~964) to preserve the deployed contract. The
  // setter was never called — collapsing to a `const` keeps the payload
  // shape intact and removes the strict-build unused-var warning.
  const aromatherapy = false;
  const [halotherapy, setHalotherapy] = useState(false);
  const [agreeToPayForGuestPass, setAgreeToPayForGuestPass] = useState(false);
  const [agreeToPayForHalotherapy, setAgreeToPayForHalotherapy] =
    useState(false);
  const [showGuestPassConfirmation, setShowGuestPassConfirmation] =
    useState(false);
  const [showHalotherapyConfirmation, setShowHalotherapyConfirmation] =
    useState(false);
  // CORP-FAMILY-008 (ADR-045 D1/D2 + A1/A2/A3 LOCKED 2026-06-15) — explicit
  // family-member corp-rate consent surface. Family members linked to an
  // active OrganizationEnrollment whose next booking will be charged at the
  // corporate rate ($20 + 8.6% AZ/Pima tax = $21.72) see an "Authorize $21.72
  // Charge" dialog BEFORE the booking submits. The Submit button cannot reach
  // bookSession until `agreeToPayForCorpRate === true`. The BE writes
  // `consentRecordedAt` server-side when `consentGiven: true` arrives on the
  // payload (A1 LOCK). Employees do NOT see this dialog (A3 LOCK — deployed
  // implicit-via-employer-relationship pattern preserved). Mirrors the
  // existing $25 retail guest-pass "Agree to Pay" consent pattern that
  // Roderick referenced as the visual reference.
  const [agreeToPayForCorpRate, setAgreeToPayForCorpRate] = useState(false);
  // Mirror ref for the consent flag — the Authorize-button onClick calls
  // setAgreeToPayForCorpRate(true) and then handleSubmit() synchronously
  // in the same tick. React batches the setState and handleSubmit reads
  // a stale closure of `agreeToPayForCorpRate === false`, which re-opens
  // the dialog and makes the click appear to do nothing. The ref is
  // updated synchronously alongside the state and is read in the
  // handleSubmit gate (line ~1025) so the same-tick submit sees the
  // correct value.
  const agreeToPayForCorpRateRef = useRef(false);
  const [showCorpRateConsent, setShowCorpRateConsent] = useState(false);
  const [addOnSummary, setAddOnSummary] = useState(null);
  // Prices for the selected day from /api/pricing (Stripe), or null while
  // they load or when they cannot be loaded.
  const [pricing, setPricing] = useState(null);
  // CORP-DOUBLE-OCC-002 (ADR-041 D3 Path A) — `eligibleAddOns` is the
  // server-rendered eligibility array (read-only contract from the
  // booking-prep / availability endpoint). The FE MUST NEVER derive
  // eligibility from client-stored fields (Scenario 1 + Scenario 2 +
  // Scenario 15 explicit). Default `[]` keeps non-corp customers on the
  // existing $25 `guest` path with no behavior change.
  const [eligibleAddOns, setEligibleAddOns] = useState([]);
  // CORP-DOUBLE-OCC-002 — toggle for the $15 corp-tier double-occupancy
  // affordance. DISJOINT from `addGuest` per ADR-042 D1 — the BE rejects
  // a submission that combines both (sessionsController returns
  // CORP_DOUBLE_OCC_CONFLICTS_WITH_ADD_GUEST). Toggling ON unchecks
  // `addGuest` and vice versa; the affordance row replaces the existing
  // $25 affordance for eligible customers (Scenario 1 "ONE option").
  const [useCorpDoubleOccupancy, setUseCorpDoubleOccupancy] = useState(false);
  const [corpGuestInfo, setCorpGuestInfo] = useState({
    name: "",
    email: "",
    phone: "",
  });
  // Surface 6 — charge-failure inline error state preserves the
  // affordance + form values so the corp member can update PM and retry
  // without re-entering guest data (Scenario 11 + 13).
  const [corpDoubleOccError, setCorpDoubleOccError] = useState(null);
  // Surface 4 — post-success acknowledgment block; rendered briefly
  // before the navigation to MyBookings. `welcomeEmailSent` only true
  // when BE returned `created: true` (Surface B per ADR-043 D3 +
  // Scenario 5 LOCKED).
  const [corpDoubleOccSuccess, setCorpDoubleOccSuccess] = useState(null);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  // IMANI-BASELINE-HYGIENE-001 (Sprint 15): refundPolicyAgreed + setter were
  // never read or set anywhere in this component (the refund policy is
  // surfaced in the CartPage flow, not BookSession). Removed to clear strict-
  // build unused-var warnings. The deployed BookSession behavior is unchanged
  // — no consumer of the BookSession surface depended on this binding.
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [isAda, setIsAda] = useState(false);
  const [bookingPrivileges, setBookingPrivileges] = useState(null);
  const [maxBookingDate, setMaxBookingDate] = useState(null);
  const [hasPriorityBooking, setHasPriorityBooking] = useState(false);
  const [purchaseChoiceModal, setPurchaseChoiceModal] = useState(null);
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);
  const autoSubmitAttemptedRef = useRef(false);
  const pendingSuiteRef = useRef(null);
  // Post-purchase round-trip target: { date: "YYYY-MM-DD", time: "HH:mm" }
  // in Phoenix time. The auto-submit fires only for this exact slot.
  const autoSubmitTargetRef = useRef(null);
  const autoSubmitTargetResolvedRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const autoSubmitRetryCountRef = useRef(0);
  const autoSubmitRetryTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Stop a pending auto-submit retry when the customer leaves the page, so
  // no booking is made in the background.
  useEffect(() => () => clearTimeout(autoSubmitRetryTimerRef.current), []);

  // Fetch user profile and add-on summary when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Fetch user profile
        const profileResponse = await profileAPI.getProfile();
        setUserProfile(profileResponse.data);
        setIsAda(profileResponse.data.ada || false);

        const fallbackPriority =
          profileResponse.data.hasPriorityBooking ||
          profileResponse.data.hasUnlimitedMembership ||
          isVipCustomer(profileResponse.data);
        const fallbackDays = fallbackPriority ? 14 : 10;
        const fallbackMaxDate = new Date();
        fallbackMaxDate.setDate(fallbackMaxDate.getDate() + fallbackDays);

        const privileges = profileResponse.data.bookingPrivileges || {
          hasPriorityBooking: fallbackPriority,
          maxBookingDays: fallbackDays,
          bookingWindowEnd: fallbackMaxDate,
          membershipAccessStatus: "active",
          membershipPauseStartsAt: null,
          membershipPauseEndsAt: null,
          membershipBookingBlocked: false,
          membershipAccessMessage: null,
          effectiveBookingCutoffDates: {
            infrared: fallbackMaxDate,
            redlight: fallbackMaxDate,
          },
          renewalCutoffDates: {
            infrared: null,
            redlight: null,
            shared: null,
          },
          bookingWindowText: `${fallbackDays} days`,
        };

        setBookingPrivileges(privileges);
        setHasPriorityBooking(Boolean(privileges.hasPriorityBooking));

        // Fetch add-on summary
        try {
          const addOnResponse = await fetch(
            `${
              process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api"
            }/add-ons/summary`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (addOnResponse.ok) {
            const addOnData = await addOnResponse.json();
            setAddOnSummary(addOnData);
          } else {
            console.error("Failed to fetch add-on summary");
          }
        } catch (addOnError) {
          console.error("Error fetching add-on summary:", addOnError);
        }
      } catch (err) {
        handleApiError(
          err,
          "BookSession.fetchUserData",
          setError,
          "Error fetching user data"
        );
        // Set default values in case of error
        setHasPriorityBooking(false);
        setBookingPrivileges(null);
        const defaultMaxDate = new Date();
        defaultMaxDate.setDate(defaultMaxDate.getDate() + 10);
        setMaxBookingDate(defaultMaxDate);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    if (!bookingPrivileges) {
      return;
    }

    if (isMassageSessionType(sessionType)) {
      setMaxBookingDate(null);
      return;
    }

    const effectiveCutoff = bookingPrivileges.bookingWindowEnd;

    if (!effectiveCutoff) {
      return;
    }

    const cutoffDate = new Date(effectiveCutoff);
    setMaxBookingDate(cutoffDate);

    setSelectedDay((currentSelectedDay) =>
      currentSelectedDay > cutoffDate ? cutoffDate : currentSelectedDay
    );
  }, [bookingPrivileges, sessionType]);

  // Load display prices for the selected day. The guest price depends on
  // the day (Wellness Wednesday).
  const pricingDateIso = moment(selectedDay)
    .tz("America/Phoenix")
    .format("YYYY-MM-DD");
  useEffect(() => {
    let cancelled = false;
    pricingAPI
      .getPricing(pricingDateIso)
      .then((response) => {
        if (!cancelled) setPricing(response.data);
      })
      .catch((err) => {
        console.error("Error loading pricing:", err);
        if (!cancelled) setPricing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pricingDateIso]);

  // Bootstrap form from URL params for the post-purchase round-trip.
  // When DropInsPage / StudioPackagesPage (via Stripe success redirect) sends
  // the user back here with ?returnTo=/book-session&sessionType=...&date=...&suite=...&time=...&autoSubmit=1,
  // preselect the form and defer an auto-submit until slots load. Without a
  // time, the form is prefilled but not submitted, so the customer is never
  // booked into a time they did not pick.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramSessionType = params.get("sessionType");
    const paramDateIso = params.get("date");
    const paramSuite = params.get("suite");
    const paramAutoSubmit = params.get("autoSubmit");
    const paramTime = params.get("time");

    if (paramSessionType && !isMassageSessionType(paramSessionType)) {
      setSessionType(paramSessionType);
    }
    if (paramDateIso) {
      const parsed = moment.tz(paramDateIso, "America/Phoenix").toDate();
      if (!Number.isNaN(parsed.getTime())) {
        setSelectedDay(parsed);
      }
    }
    if (paramSuite) {
      setSelectedSuite(paramSuite);
      pendingSuiteRef.current = paramSuite;
    }
    if (paramAutoSubmit === "1" && paramSessionType && paramDateIso) {
      if (isPhoenixTime(paramTime)) {
        autoSubmitTargetRef.current = { date: paramDateIso, time: paramTime };
        setPendingAutoSubmit(true);
        setTermsAgreed(true);
      } else {
        setMessage("Your purchase is complete. Choose a time to finish your booking.");
      }
    }
    // run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //console.log("DEBUGGGG:::ADA is to ==>", isAda);

  // Fetch available time slots when sessionType or selectedDay changes.
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        // Ensure selectedDay is not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDay < today) {
          setSelectedDay(today);
          return;
        }

        const year = selectedDay.getFullYear();
        const month = String(selectedDay.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDay.getDate()).padStart(2, "0");
        const dateString = `${year}-${month}-${day}`;

        /*console.log(
          "Fetching availability for date:",
          dateString,
          "and sessionType:",
          sessionType
        );*/

        const response = await bookingsAPI.getAvailableSessions(
          dateString,
          sessionType
        );

        // Backend already filters out past slots using Phoenix timezone
        // No additional filtering needed on frontend
        const slots = response.data.availableSlots.map(
          (slot) => new Date(slot)
        );

        setAvailableSlots(slots);

        // CORP-DOUBLE-OCC-002 (ADR-041 D3 Path A) — server-rendered
        // eligibility array. Defaults to `[]` when the BE does not
        // return the additive field (forward-compat: pre-PR-#212
        // deployments OR sessionType !== "infrared" both yield `[]`).
        // The FE NEVER infers eligibility from client-stored fields.
        const nextEligibleAddOns = Array.isArray(
          response.data.eligibleAddOns
        )
          ? response.data.eligibleAddOns
          : [];
        setEligibleAddOns(nextEligibleAddOns);
        // Scenario 15 — session-type-aware affordance display. If the
        // session type / day no longer surfaces `corpDoubleOccupancy`,
        // collapse any in-progress corp affordance state. (E.g., user
        // toggled it on for an infrared slot, then switched to redlight
        // or to an ineligible date — the affordance disappears AND the
        // toggle resets so a stale `doubleOccupancy: true` cannot ride
        // along on submit.)
        if (!nextEligibleAddOns.includes("corpDoubleOccupancy")) {
          setUseCorpDoubleOccupancy(false);
          setCorpGuestInfo({ name: "", email: "", phone: "" });
          setCorpDoubleOccError(null);
        }

        // Post-purchase round-trip: select the exact slot the customer
        // picked before checkout. If it is gone, stop the auto-submit and
        // ask the customer to pick again.
        const target = autoSubmitTargetRef.current;
        const isTargetDay =
          target &&
          !autoSubmitTargetResolvedRef.current &&
          moment(selectedDay).tz("America/Phoenix").format("YYYY-MM-DD") ===
            target.date;
        if (isTargetDay) {
          autoSubmitTargetResolvedRef.current = true;
          const match = slots.find(
            (slot) =>
              moment(slot).tz("America/Phoenix").format("HH:mm") ===
              target.time
          );
          if (match) {
            setAppointmentDate(match);
          } else {
            setAppointmentDate(slots.length > 0 ? slots[0] : null);
            setPendingAutoSubmit(false);
            setError(
              `The time you chose (${moment
                .tz(target.time, "HH:mm", "America/Phoenix")
                .format("h:mm A")}) is no longer available. Choose another time to finish your booking.`
            );
          }
        } else if (slots.length > 0) {
          // If slots exist, default the appointment date to the first available slot.
          setAppointmentDate(slots[0]);
        } else {
          setAppointmentDate(null);
        }
      } catch (err) {
        handleApiError(
          err,
          "BookSession.fetchAvailability",
          setError,
          "Error fetching availability"
        );
      }
    };
    fetchAvailability();
  }, [sessionType, selectedDay]);

  // Filter available suites based on ADA status
  useEffect(() => {
    if (availableSuites.length > 0) {
      setFilteredSuites(availableSuites);
    } else {
      setFilteredSuites([]);
    }
  }, [availableSuites, isAda, appointmentDate]);

  // Fetch available suites when sessionType, selectedDay, or appointmentDate changes.
  useEffect(() => {
    const fetchSuites = async () => {
      if (isMassageSessionType(sessionType)) {
        setAvailableSuites([]);
        setFilteredSuites([]);
        setSelectedSuite(appointmentDate ? "1" : "");
        return;
      }

      try {
        const year = selectedDay.getFullYear();
        const month = String(selectedDay.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDay.getDate()).padStart(2, "0");
        const dateString = `${year}-${month}-${day}`;
        if (!appointmentDate) return;

        // Format appointmentDate as a Phoenix local time string (HH:mm)
        const phoenixTime = moment(appointmentDate)
          .tz("America/Phoenix")
          .format("HH:mm");

        /*console.log(
          "Fetching suites with date:",
          dateString,
          "time:",
          phoenixTime,
          "for sessionType:",
          sessionType
        );*/

        let response;
        if (sessionType === "infrared") {
          response = await suitesAPI.getSaunaAvailability(
            dateString,
            phoenixTime,
            isAda // Pass isAda flag
          );
        } else {
          response = await suitesAPI.getRedLightAvailability(
            dateString,
            phoenixTime,
            isAda // Pass isAda flag
          );
        }

        /*console.log("Suites response:", response.data);
        console.log("DEBUG::Available suites response:", response.data.suites);*/

        // Sort suites by suiteNumber in ascending order
        const sortedSuites = (response.data.suites || []).sort(
          (a, b) => a.suiteNumber - b.suiteNumber
        );
        setAvailableSuites(sortedSuites);
        // Honor a pending post-purchase suite preselection (URL param) if
        // the suite is actually available for the fetched date/time; otherwise
        // clear so the user picks manually.
        const pending = pendingSuiteRef.current;
        const pendingStillAvailable =
          pending &&
          sortedSuites.some(
            (s) => String(s.id) === String(pending) ||
              String(s.suiteNumber) === String(pending)
          );
        if (pendingStillAvailable) {
          setSelectedSuite(String(pending));
        } else {
          setSelectedSuite("");
        }
        // On a post-purchase round-trip, keep the pending suite until the
        // chosen slot is resolved; an earlier fetch for a default slot must
        // not use it up.
        if (
          !autoSubmitTargetRef.current ||
          autoSubmitTargetResolvedRef.current
        ) {
          pendingSuiteRef.current = null;
        }
      } catch (err) {
        handleApiError(
          err,
          "BookSession.fetchSuites",
          () => {
            console.error("Error fetching available suites:", err);
            setAvailableSuites([]);
          },
          "Error fetching available suites"
        );
      }
    };
    fetchSuites();
  }, [sessionType, selectedDay, appointmentDate, isAda]);

  // After a post-purchase round-trip (autoSubmit=1), wait for availability +
  // suite selection to settle, then fire handleSubmit exactly once. The URL is
  // scrubbed via navigate(..., { replace: true }) before submit so that browser
  // back navigation does not replay the booking.
  useEffect(() => {
    if (!pendingAutoSubmit) return;
    if (autoSubmitAttemptedRef.current) return;
    if (!appointmentDate) return;
    // Submit only the exact slot the customer picked before checkout.
    const target = autoSubmitTargetRef.current;
    if (!target || !autoSubmitTargetResolvedRef.current) return;
    const chosen = moment(appointmentDate).tz("America/Phoenix");
    if (
      chosen.format("YYYY-MM-DD") !== target.date ||
      chosen.format("HH:mm") !== target.time
    ) {
      return;
    }
    // For non-massage sessions we need a suite to be resolved.
    if (!isMassageSessionType(sessionType) && !selectedSuite) return;
    autoSubmitAttemptedRef.current = true;
    // Clear auto-submit and context params from URL before submit so reload
    // or history-back does not re-fire the booking.
    navigate(location.pathname, { replace: true });
    setPendingAutoSubmit(false);
    // Fire the submit on the next tick so React has applied the navigate.
    // No cleanup: setPendingAutoSubmit(false) re-runs this effect, and a
    // clearTimeout cleanup would cancel the submit before it fires.
    // autoSubmitAttemptedRef already prevents a second submit.
    setTimeout(() => {
      handleSubmitRef.current?.({ preventDefault: () => {}, isAutoSubmit: true });
    }, 0);
    // handleSubmit is read through handleSubmitRef so the submit uses the
    // latest state; it is intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingAutoSubmit,
    appointmentDate,
    selectedSuite,
    sessionType,
    location.pathname,
    navigate,
  ]);

  // Auto-submit found no credit yet: retry while the purchase is processed.
  // Never send a customer who just paid back to a purchase page.
  const retryAutoSubmitOrStop = () => {
    if (autoSubmitRetryCountRef.current < AUTO_SUBMIT_MAX_RETRIES) {
      autoSubmitRetryCountRef.current += 1;
      setError("");
      setMessage("Your purchase is still being processed. Finishing your booking...");
      autoSubmitRetryTimerRef.current = setTimeout(() => {
        handleSubmitRef.current?.({ preventDefault: () => {}, isAutoSubmit: true });
      }, AUTO_SUBMIT_RETRY_MS);
      return;
    }
    setMessage("");
    setError(
      "We could not confirm your purchase yet. Wait a minute, then click Book Session. You do not need to pay again."
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    // Hoisted so the catch block can include it in the purchase-required modal
    // context (re-submit path for "continue at corporate rate").
    let bookingData = null;

    // Validate required fields.
    if (!appointmentDate) {
      setError("Please select a time slot.");
      return;
    }
    if (!isMassageSessionType(sessionType) && !selectedSuite) {
      setError("Please select an available suite.");
      return;
    }
    if (new Date(appointmentDate) < new Date()) {
      setError(
        "The selected time is in the past. Please choose a future time slot."
      );
      return;
    }

    // Check if user has agreed to terms and refund policy
    if (!termsAgreed) {
      setError(
        "You must agree to the Terms of Service and Studio Policy before booking a session."
      );
      return;
    }

    try {
      // Fetch the user's profile to check membership, package, and drop-in counts.
      const profileRes = await profileAPI.getProfile();
      const profileData = profileRes.data;
      const corporateStatus = profileData.corporateProgramStatus || null;
      const nextCorporateAmountDue = Number(
        corporateStatus?.nextBookingAmountDue || 0
      );
      const personalCreditSourceForBooking = getPersonalCreditSource(
        profileData,
        sessionType,
        appointmentDate
      );
      const isMassageBooking = isMassageSessionType(sessionType);
      const massagePricing = getMassagePriceDisplay(pricing, sessionType);
      const massagePrice = massagePricing?.price || null;
      const massageTotal = massagePricing?.total || 0;

      // Validation check for contact number
      if (!profileData.contactNumber) {
        setError(
          "Please add a contact phone number to your profile to receive important information about your bookings."
        );
        setTimeout(() => navigate("/profile"), 1500);
        return;
      }

      // ESIGN-WAIVER-CONSENT-001 first-booking gate (Zuri spec §6.2 + §16.2).
      // The gate sits between session-selection and payment, BEFORE payment-
      // method validation, so customers who haven't signed don't see a
      // payment-method error first. Returning customers (already signed) skip
      // straight through this check with no UX impact. Staff/admin booking on
      // behalf of a member route through their own gate the same way; backend
      // RBAC on POST /api/waivers/initiate handles the cross-user case.
      try {
        const waiverResp = await getMyWaiverStatus();
        if (!hasFullySignedWaivers(waiverResp.data)) {
          // Preserve current booking state in the URL so the customer can
          // resume after signing. Reuses the existing returnTo pattern that
          // BookSession already supports for post-purchase resume.
          const params = new URLSearchParams();
          params.set("returnTo", "/book-session");
          if (sessionType) params.set("sessionType", sessionType);
          navigate(`${WAIVER_GATE_PATH}?${params.toString()}`);
          return;
        }
      } catch (waiverErr) {
        // Defensive: a transient /api/waivers/me failure should not silently
        // let the booking proceed (would let an unsigned customer book), but
        // it ALSO should not hard-block on every blip. Show a non-fatal
        // message and surface a retry. The booking flow is paused; the user
        // can resubmit after the network recovers.
        setError(
          "We couldn't verify your consent forms. Please try again. If this keeps happening, email support@divitiadorastudio.com."
        );
        return;
      }

      // Check if the user has a payment method
      if (
        !profileData.paymentMethod &&
        (isMassageBooking ||
          !corporateStatus?.enrolled ||
          (nextCorporateAmountDue > 0 && !personalCreditSourceForBooking))
      ) {
        setError(
          isMassageBooking
            ? `A saved payment method is required before booking ${getSessionDisplayName(
                sessionType
              ).toLowerCase()}${
                massagePrice ? ` at $${massageTotal.toFixed(2)} (incl. tax)` : ""
              }.`
            : corporateStatus?.enrolled &&
          nextCorporateAmountDue > 0 &&
          !personalCreditSourceForBooking
            ? `${
                corporateStatus?.organizationCode || "Your corporate program"
              } requires a saved payment method before employee-paid sessions can be booked at the $${nextCorporateAmountDue} corporate rate.`
            : "You need to add a payment method before booking a session. Please add a payment method in your profile."
        );
        setTimeout(() => navigate("/payment"), 1500);
        return;
      }

      // Determine if the user is allowed to book the session based on service type.
      let validBooking = false;
      let creditTypeToUse = "";

      if (isMassageBooking) {
        validBooking = true;
        creditTypeToUse = `${getSessionDisplayName(sessionType)}${
          massagePrice ? ` ($${massageTotal.toFixed(2)} incl. tax)` : ""
        }`;
      } else if (
        corporateStatus?.enrolled &&
        corporateStatus.nextBookingSourceType === "organization-sponsored"
      ) {
        validBooking = true;
        creditTypeToUse = `${corporateStatus.organizationCode} Sponsored Session`;
      } else if (personalCreditSourceForBooking) {
        validBooking = true;
        creditTypeToUse = personalCreditSourceForBooking.description;
      } else if (corporateStatus?.enrolled) {
        // CORP-FAMILY-007 (GAP-11 / Scenario 3) — proactive client-side
        // eligibility check for corp-rate bookings. If the program's
        // `eligibleSessionTypes` does NOT cover the current `sessionType`,
        // the corp rate is unusable for this booking and the BE will
        // reject at submit. Surface a friendlier message that NAMES the
        // eligible types instead of letting the user submit blindly.
        //
        // Applies to BOTH employees and family members — the eligibility
        // contract is property of the OrganizationProgram, not the role
        // (per Marcus ADR-038). Family members are the proximate trigger
        // because the deployed family-member journey wasn't reaching
        // this branch at all before Simone's PR #220 fix (the BE was
        // returning `corporateProgramStatus: null` for family members,
        // sending them down the no-credits fall-through). Now that
        // family members get `enrolled: true`, this branch fires for them
        // too — and the eligibility-mismatch case becomes reachable.
        //
        // Defensive: when `eligibleSessionTypes` is missing or empty on
        // the corporate-status payload, fall through to the original
        // happy-path corp-rate branch (backward-compatible with any
        // employee response shape that omits the field).
        const eligibleTypes = corporateStatus.eligibleSessionTypes || [];
        if (
          eligibleTypes.length > 0 &&
          !eligibleTypes.includes(sessionType)
        ) {
          const formatTypes = (types) =>
            types
              .map((t) => getSessionDisplayName(t).toLowerCase())
              .join(", ");
          setError(
            `Your ${corporateStatus.organizationCode} benefit covers ${formatTypes(
              eligibleTypes
            )} sessions only. For ${getSessionDisplayName(
              sessionType
            ).toLowerCase()} sessions, please purchase a drop-in or use a personal credit.`
          );
          setTimeout(() => navigate(getDropInPurchasePath()), 1500);
          return;
        }
        validBooking = true;
        creditTypeToUse = `${corporateStatus.organizationCode} Corporate Rate ($${corporateStatus.corporateRate})`;
      } else if (sessionType === "infrared") {
        // Check for available sessions in priority order: 1. gifted credits, 2. drop-ins, 3. package, 4. membership
        if (profileData.giftedSaunaCredits?.remaining > 0) {
          validBooking = true;
          creditTypeToUse = "Gifted Credits";
        } else if (profileData.dropInSaunaCount?.remaining > 0) {
          validBooking = true;
          creditTypeToUse = "Drop-in Credits";
        } else if (
          profileData.saunaPackageName &&
          profileData.saunaPackageName !== "none" &&
          profileData.saunaPackageSessionUsage?.remaining > 0
        ) {
          validBooking = true;
          creditTypeToUse = `Package: ${getPackageFriendlyName(profileData.saunaPackageName)}`;
        } else if (
          profileData.creditSystemType === "unified_shared" &&
          profileData.sharedMembershipSessionUsage?.remaining > 0
        ) {
          validBooking = true;
          creditTypeToUse = "Membership: Flexible Session Credits";
        } else if (
          profileData.saunaMembershipType &&
          profileData.saunaMembershipType !== "none" &&
          profileData.saunaMembershipSessionUsage?.remaining > 0
        ) {
          validBooking = true;
          creditTypeToUse = `Membership: ${getMembershipFriendlyName(profileData.saunaMembershipType)}`;
        } else {
          if (e?.isAutoSubmit) {
            retryAutoSubmitOrStop();
            return;
          }
          setError(
            "You don't have any valid sauna sessions available. Please purchase a membership, package, or drop-in session."
          );
          setTimeout(() => navigate(getDropInPurchasePath()), 1500);
          return;
        }
      } else if (sessionType === "redlight") {
        // Check for available sessions in priority order: 1. gifted credits, 2. drop-ins, 3. package, 4. membership
        if (profileData.giftedRedLightCredits?.remaining > 0) {
          validBooking = true;
          creditTypeToUse = "Gifted Credits";
        } else if (profileData.dropInRedLightCount?.remaining > 0) {
          validBooking = true;
          creditTypeToUse = "Drop-in Credits";
        } else if (
          profileData.redLightPackageName &&
          profileData.redLightPackageName !== "none" &&
          profileData.redLightPackageSessionUsage?.remaining > 0
        ) {
          validBooking = true;
          creditTypeToUse = `Package: ${getPackageFriendlyName(profileData.redLightPackageName)}`;
        } else if (
          profileData.creditSystemType === "unified_shared" &&
          profileData.sharedMembershipSessionUsage?.remaining > 0
        ) {
          validBooking = true;
          creditTypeToUse = "Membership: Flexible Session Credits";
        } else if (
          profileData.redLightMembershipType &&
          profileData.redLightMembershipType !== "none" &&
          profileData.redLightMembershipSessionUsage?.remaining > 0
        ) {
          validBooking = true;
          creditTypeToUse = `Membership: ${getMembershipFriendlyName(profileData.redLightMembershipType)}`;
        } else {
          if (e?.isAutoSubmit) {
            retryAutoSubmitOrStop();
            return;
          }
          setError(
            "You don't have any valid red light sessions available. Please purchase a membership, package, or drop-in session."
          );
          setTimeout(() => navigate(getDropInPurchasePath()), 1500);
          return;
        }
      }

      // Set a message to inform the user which credit type will be used
      setMessage(`Booking with ${creditTypeToUse}`);

      if (!validBooking) {
        if (e?.isAutoSubmit) {
          retryAutoSubmitOrStop();
          return;
        }
        setError(
          "No valid membership, package, or drop-in sessions found for booking."
        );
        setTimeout(() => navigate(getDropInPurchasePath()), 1500);
        return;
      }

      // Proceed with booking.
      // Validate guest information if adding a guest
      if (!isMassageBooking && addGuest) {
        if (!guestInfo.name || !guestInfo.email || !guestInfo.phone) {
          setError(
            "Please provide complete guest information (name, email, and phone)."
          );
          return;
        }
      }

      // CORP-FAMILY-008 (ADR-045 D1/D2 + A1/A2/A3 LOCKED 2026-06-15) —
      // family-member corp-rate explicit consent gate. Family members whose
      // next booking will hit the corporate rate (not sponsored, not a
      // personal credit) see an "Authorize $21.72 charge" dialog BEFORE the
      // booking submits. The gate fires ONLY when the cascade has resolved
      // to organization-corporate-rate AND the user's corpRole is
      // "family_member" (A3 LOCK — employees do NOT see this dialog; the
      // deployed implicit-via-employer-relationship pattern is preserved).
      //
      // Mirrors the existing $25 retail guest-pass "Agree to Pay" consent
      // pattern (showGuestPassConfirmation / agreeToPayForGuestPass) that
      // Roderick referenced as the visual reference. On confirm, the
      // dialog flips `agreeToPayForCorpRate` to true and the user re-clicks
      // Book Session to fire the actual submit — same UX shape as the
      // existing guest-pass / halotherapy "Agree to Pay" flows.
      //
      // The BE accepts `consentGiven: true` on the booking payload (added
      // below) and writes `consentRecordedAt` server-side (A1 LOCK). A
      // payload that arrives with `consentGiven !== true` on a family-
      // member corp-rate booking is rejected 400 by the BE; the catch
      // block below renders the error inline. Consumers should never see
      // this defensive path if the FE gate is intact, but it is here for
      // safety against any future cascade-bypass.
      const isFamilyMemberCorpRate =
        !isMassageBooking &&
        corporateStatus?.enrolled &&
        corporateStatus.corpRole === "family_member" &&
        corporateStatus.nextBookingSourceType === "organization-corporate-rate" &&
        !personalCreditSourceForBooking;
      if (
        isFamilyMemberCorpRate &&
        !agreeToPayForCorpRateRef.current &&
        !agreeToPayForCorpRate
      ) {
        setShowCorpRateConsent(true);
        return;
      }

      // CORP-DOUBLE-OCC-002 (Scenarios 4 + 8) — validate the corp-tier
      // guest form before sending the submit. Defense in depth — the
      // submit button is already disabled when the form is invalid
      // (see render block), but a keyboard-driven submit or a programmatic
      // submit must still hit this gate. DISJOINT from `addGuest` per
      // ADR-042 D1: only one of `addGuest` / `useCorpDoubleOccupancy`
      // can be active at a time (the toggle handlers enforce this).
      if (useCorpDoubleOccupancy) {
        if (!isCorpGuestFormValid(corpGuestInfo)) {
          setError(
            "Please provide complete guest information (name, valid email, and valid phone)."
          );
          return;
        }
      }

      bookingData = {
        sessionType,
        appointmentDate: moment(appointmentDate)
          .tz("America/Phoenix")
          .toISOString(),
        suite: isMassageBooking ? 1 : Number(selectedSuite),
        addGuest,
        guestInformation: useCorpDoubleOccupancy
          ? corpGuestInfo
          : addGuest
          ? guestInfo
          : undefined,
        useGuestPass: addGuest && hasGuestPasses && !agreeToPayForGuestPass, // Use guest pass if available and not paying
        payForGuestPass: addGuest && agreeToPayForGuestPass, // Flag to indicate user agreed to pay for guest pass
        aromatherapy,
        halotherapy,
        useHalotherapyEntitlement:
          halotherapy &&
          hasHalotherapyEntitlement &&
          !agreeToPayForHalotherapy, // Use halotherapy entitlement if available and not paying
        payForHalotherapy: halotherapy && agreeToPayForHalotherapy, // Flag to indicate user agreed to pay for halotherapy
        consentToCommunications: termsAgreed,
        // CORP-DOUBLE-OCC-002 (ADR-040/041/042/043) — additive booking
        // submit fields. `doubleOccupancy: true` triggers the BE
        // eligibility gate + ADR-043 D2 `findOrCreateGuestUser`-first
        // ordering + $15 corp-tier charge + SessionBooking field
        // population (guestUserId, broughtBy, corpBookingRole,
        // corpEnrollmentId). DISJOINT from `addGuest` per ADR-042 D1;
        // BE returns CORP_DOUBLE_OCC_CONFLICTS_WITH_ADD_GUEST if both
        // arrive. `guestInformation` (above) carries name+email+phone
        // for the BE auto-account-create helper (Simone's 002-BE
        // supplement PR consumes this).
        doubleOccupancy: useCorpDoubleOccupancy,
        // CORP-FAMILY-008 (ADR-045 + A1 LOCKED 2026-06-15) — explicit
        // family-member corp-rate consent. Only set when this is a
        // family-member booking that resolved to the corporate rate AND
        // the customer toggled the consent dialog (handled above). The BE
        // accepts the flag and persists `consentRecordedAt` on the
        // SessionBooking server-side. Employees do NOT set this flag
        // (A3 LOCK — the deployed implicit-consent path is preserved).
        consentGiven: isFamilyMemberCorpRate ? true : undefined,
      };

      setSubmitInFlight(true);
      setCorpDoubleOccError(null);
      const response = await bookingsAPI.bookSession(bookingData);
      setMessage(response.data.message || "Session booked successfully!");

      // Surface 4 — post-success confirmation block. Only renders when
      // the corp affordance was active; non-corp bookings keep their
      // existing transient success message + navigation behavior.
      // `welcomeEmailSent` reads from the BE response — Simone's 002-BE
      // returns `guestAccountCreated: true` (or similar) when the
      // helper auto-created a new guest User per Scenario 8 + 9.
      // Forward-compat: if the BE doesn't surface the flag yet, the
      // block renders the line items + guest line without the welcome
      // acknowledgment (per Scenario 5 Surface B conditional).
      if (useCorpDoubleOccupancy) {
        setCorpDoubleOccSuccess({
          guestName: corpGuestInfo.name,
          guestEmail: corpGuestInfo.email,
          welcomeEmailSent: Boolean(
            response.data.guestAccountCreated === true ||
              response.data.welcomeEmailSent === true
          ),
        });
      }

      if (typeof onBookingSuccess === "function") {
        setTimeout(() => {
          onBookingSuccess(response.data);
        }, 900);
      } else {
        setTimeout(() => {
          navigate("/member?view=calendar");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 900);
      }
      setSubmitInFlight(false);
    } catch (err) {
      setSubmitInFlight(false);
      const errorMessage = handleApiError(
        err,
        "BookSession.handleSubmit",
        setError,
        "Error booking session. Please try again."
      );

      const nextAction = err.response?.data?.nextAction;
      const errorCode = err.response?.data?.code;
      const errorContext = err.response?.data?.context;

      // CORP-DOUBLE-OCC-002 — Surface 6 inline charge-failure state.
      // Scenarios 11 + 13: BE atomicity guarantees the SessionBooking
      // is NOT persisted on charge failure; the FE preserves the
      // affordance + form values so the corp member can update PM and
      // retry without re-entering guest data. The standalone error
      // message banner remains (`setError` above) — this block adds the
      // structured inline error UI with the retry actions.
      if (useCorpDoubleOccupancy) {
        setCorpDoubleOccError({
          title: corpDoubleOccPriceText
            ? `Unable to charge ${corpDoubleOccPriceText} for the guest add-on`
            : "Unable to charge for the guest add-on",
          body:
            "Please update your payment method and try again. Your guest's information has been kept.",
          rawMessage: errorMessage,
        });
      }

      // CORP-FAMILY-008 (defensive) — if the BE rejects a family-member
      // corp-rate booking for missing/invalid consent, reset the FE
      // consent flag and reopen the dialog so the user can re-authorize
      // explicitly. Consumers should never reach this branch when the FE
      // gate is intact (the consentGiven flag is only sent when the
      // dialog was confirmed), but it is here for safety against any
      // future cascade-bypass or BE-contract drift. The error message
      // from handleApiError surfaces via setError above.
      //
      // BE contract (Simone's CORP-FAMILY-008 BE PR #223 /
      // organizationBookingFlowService.js): the canonical error code is
      // `CORP_RATE_FAMILY_MEMBER_CONSENT_REQUIRED` (400, expose:true). The
      // older `CORP_FAMILY_CONSENT_REQUIRED` / `CORP_FAMILY_CONSENT_MISSING`
      // names are accepted defensively in case the contract evolves; the
      // canonical name is the one BE throws today.
      if (
        typeof errorCode === "string" &&
        (errorCode === "CORP_RATE_FAMILY_MEMBER_CONSENT_REQUIRED" ||
          errorCode === "CORP_FAMILY_CONSENT_REQUIRED" ||
          errorCode === "CORP_FAMILY_CONSENT_MISSING")
      ) {
        agreeToPayForCorpRateRef.current = false;
        setAgreeToPayForCorpRate(false);
        setShowCorpRateConsent(true);
      }

      if (
        nextAction === "/payment" &&
        typeof errorCode === "string" &&
        (errorCode.startsWith("CORPORATE_RATE_") ||
          errorCode.startsWith("MASSAGE_PAYMENT_") ||
          errorCode.startsWith("SESSION_ADD_ON_"))
      ) {
        setMessage("");
        setError(errorMessage);
        setTimeout(() => navigate(nextAction), 1800);
      } else if (errorCode === "BOOKING_PURCHASE_REQUIRED") {
        // Post-renewal booking with no credits available. Backend no longer
        // dictates drop-in vs package — let the user choose. If the customer
        // is enrolled in a corporate-rate program, a third option ("continue
        // at $X corporate rate") is surfaced via err.response.data.corporateRate
        // and re-submits with acknowledgedPastRenewalCharge=true.
        setMessage("");
        setError(errorMessage);
        const ctx = errorContext || {};
        const corporateRate = err.response?.data?.corporateRate || null;
        // The backend sends attemptedDateIso as a full UTC timestamp. The
        // round-trip needs the Phoenix YYYY-MM-DD date and HH:mm time, or
        // the choice page drops the whole booking-return context.
        const attemptedIso = ctx.attemptedDateIso;
        const attemptedIsDateOnly =
          typeof attemptedIso === "string" &&
          /^\d{4}-\d{2}-\d{2}$/.test(attemptedIso);
        const attemptedSlot =
          attemptedIso && !attemptedIsDateOnly
            ? moment(attemptedIso).tz("America/Phoenix")
            : null;
        const chosenSlot = appointmentDate
          ? moment(appointmentDate).tz("America/Phoenix")
          : null;
        let dateIso = toIsoDateOnly(appointmentDate || selectedDay);
        if (attemptedIsDateOnly) {
          dateIso = attemptedIso;
        } else if (attemptedSlot?.isValid()) {
          dateIso = attemptedSlot.format("YYYY-MM-DD");
        } else if (chosenSlot) {
          dateIso = chosenSlot.format("YYYY-MM-DD");
        }
        let time = "";
        if (attemptedSlot?.isValid()) {
          time = attemptedSlot.format("HH:mm");
        } else if (chosenSlot) {
          time = chosenSlot.format("HH:mm");
        }
        setPurchaseChoiceModal({
          sessionType: ctx.attemptedSessionType || sessionType,
          dateIso,
          suite: ctx.attemptedSuite || String(selectedSuite || ""),
          time,
          corporateRate,
          bookingData,
        });
      }
    }
  };
  handleSubmitRef.current = handleSubmit;

  const buildReturnToQuery = (ctx) => {
    const params = new URLSearchParams();
    params.set("returnTo", "/book-session");
    if (ctx.sessionType) params.set("sessionType", ctx.sessionType);
    if (ctx.dateIso) params.set("date", ctx.dateIso);
    if (ctx.suite) params.set("suite", String(ctx.suite));
    if (ctx.time) params.set("time", ctx.time);
    return params.toString();
  };

  const handlePurchaseChoice = (path) => {
    if (!purchaseChoiceModal) return;
    // ADR-027 §A1.4(b): the booking-return context rides the URL to the
    // drop-in/package choice page (returnTo/sessionType/date/suite) — the
    // legacy browser-storage stash is removed (the R-020 paid-but-stranded
    // vector). The choice page reads these URL params into the cart context,
    // which CartPage forwards to the backend as bookingReturn so success_url
    // round-trips them across the external Stripe redirect.
    const query = buildReturnToQuery(purchaseChoiceModal);
    setPurchaseChoiceModal(null);
    navigate(`${path}?${query}`);
  };

  const handleContinueAtCorporateRate = async () => {
    if (!purchaseChoiceModal?.bookingData || !purchaseChoiceModal?.corporateRate) {
      return;
    }
    const amount = purchaseChoiceModal.corporateRate.amountDue;
    setPurchaseChoiceModal(null);
    setError("");
    setMessage(
      amount != null
        ? `Booking at $${amount} corporate rate…`
        : "Booking at corporate rate…"
    );
    try {
      const response = await bookingsAPI.bookSession({
        ...purchaseChoiceModal.bookingData,
        acknowledgedPastRenewalCharge: true,
      });
      setMessage(response.data.message || "Session booked successfully!");
      if (typeof onBookingSuccess === "function") {
        setTimeout(() => {
          onBookingSuccess(response.data);
        }, 900);
      } else {
        setTimeout(() => {
          navigate("/member?view=calendar");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 900);
      }
    } catch (err) {
      handleApiError(
        err,
        "BookSession.handleContinueAtCorporateRate",
        setError,
        "Error booking session. Please try again."
      );
      setMessage("");
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate("/member");
  };
  const renewalCutoff =
    bookingPrivileges?.renewalCutoffDates?.[sessionType] || null;
  const bookingWindowEnd = bookingPrivileges?.bookingWindowEnd
    ? new Date(bookingPrivileges.bookingWindowEnd)
    : null;
  const renewalLimitsBookingWindow =
    renewalCutoff &&
    bookingWindowEnd &&
    new Date(renewalCutoff).getTime() < bookingWindowEnd.getTime();
  const bookingWindowDescription = hasPriorityBooking
    ? "14 days in advance with Priority booking"
    : "10 days in advance";
  const membershipAccessStatus =
    bookingPrivileges?.membershipAccessStatus || "active";
  const membershipAccessMessage =
    bookingPrivileges?.membershipAccessMessage || null;
  const corporateProgramStatus = userProfile?.corporateProgramStatus || null;
  const corporateProgramNextRate = Number(
    corporateProgramStatus?.nextBookingAmountDue || 0
  );
  const personalCreditSource = getPersonalCreditSource(
    userProfile,
    sessionType,
    appointmentDate || selectedDay
  );
  const isMassageBooking = isMassageSessionType(sessionType);
  const massagePricing = getMassagePriceDisplay(pricing, sessionType);
  const massagePrice = massagePricing?.price || null;
  const massageTax = massagePricing?.tax || 0;
  const massageTotal = massagePricing?.total || 0;
  // Add-on prices for the selected day (Wellness Wednesday guest price on a
  // Wednesday), from Stripe through /api/pricing.
  const guestPriceText = formatPriceItem(pricing?.addOns?.guest);
  const halotherapyPriceText = formatPriceItem(pricing?.addOns?.halotherapy);
  const corpDoubleOccPriceText = formatPriceItem(
    pricing?.addOns?.corpDoubleOccupancy
  );
  const hasGuestPasses =
    addOnSummary && addOnSummary.data && addOnSummary.data.guestPasses > 0;
  const hasHalotherapyEntitlement =
    addOnSummary && addOnSummary.data && addOnSummary.data.halotherapy > 0;
  const inlineChargeItems = [];
  if (
    sessionType === "infrared" &&
    addGuest &&
    !hasGuestPasses &&
    agreeToPayForGuestPass
  ) {
    inlineChargeItems.push({
      label: pricing?.isWellnessWednesday
        ? "Guest add-on (Wellness Wednesday)"
        : "Guest add-on",
      amountCents: pricing?.addOns?.guest?.amountCents ?? null,
    });
  }
  if (
    sessionType === "infrared" &&
    halotherapy &&
    !hasHalotherapyEntitlement &&
    agreeToPayForHalotherapy
  ) {
    inlineChargeItems.push({
      label: "Halotherapy add-on",
      amountCents: pricing?.addOns?.halotherapy?.amountCents ?? null,
    });
  }
  // CORP-DOUBLE-OCC-002 (ADR-040 D2 — inline-charge train).
  // The corp-tier double-occupancy add-on rides the same inline
  // booking-charge summary as the guest + halotherapy paths so the corp
  // member sees the explicit pre-submit total.
  if (sessionType === "infrared" && useCorpDoubleOccupancy) {
    inlineChargeItems.push({
      label: "+1 Double Occupancy",
      amountCents: pricing?.addOns?.corpDoubleOccupancy?.amountCents ?? null,
    });
  }
  // Tax is rounded once on the subtotal, the same way the backend charges.
  const inlineChargePricesReady = inlineChargeItems.every(
    (item) => item.amountCents != null
  );
  const inlineChargeSubtotalCents = inlineChargeItems.reduce(
    (sum, item) => sum + (item.amountCents || 0),
    0
  );
  const inlineChargeTaxCents = Math.round(
    inlineChargeSubtotalCents *
      (pricing?.taxRate ?? ARIZONA_PIMA_SALES_TAX_RATE)
  );
  const inlineChargeTax = centsToDollars(inlineChargeTaxCents);
  const totalInlineCharge = centsToDollars(
    inlineChargeSubtotalCents + inlineChargeTaxCents
  );
  const corporateBookingMessage = !corporateProgramStatus?.enrolled
    ? null
    : corporateProgramStatus.nextBookingSourceType === "organization-sponsored"
    ? corporateProgramStatus.nextBookingMessage
    : personalCreditSource
    ? `${corporateProgramStatus.organizationCode}'s sponsored sessions are unavailable for this ${
        getSessionServiceName(sessionType)
      } booking, so ${personalCreditSource.description.toLowerCase()} will be used before the $${corporateProgramStatus.corporateRate} corporate rate.`
    : corporateProgramStatus.nextBookingMessage;
  // CORP-FAMILY-008 (ADR-045 + A1/A2/A3 LOCKED 2026-06-15) — render-time
  // family-member corp-rate consent gate derivations. The render uses these
  // to (a) decide whether to show the consent dialog and (b) compute the
  // tax-breakdown display ($20.00 base + $1.72 tax = $21.72). Note: the BE
  // is the authoritative source for the actual charge math — it routes via
  // the pinned Stripe Price ID per ADR-045 D1 and applies tax via the
  // pinned `tax_rates` per D2. The FE breakdown is for customer
  // transparency only; do NOT trust client-computed totals for BE math.
  const corpRateForConsent = Number(corporateProgramStatus?.corporateRate || 0);
  const corpRateTax = Number(
    (corpRateForConsent * ARIZONA_PIMA_SALES_TAX_RATE).toFixed(2)
  );
  const corpRateTotal = Number((corpRateForConsent + corpRateTax).toFixed(2));
  const isFamilyMemberCorpRateUser =
    !isMassageBooking &&
    corporateProgramStatus?.enrolled &&
    corporateProgramStatus.corpRole === "family_member" &&
    corporateProgramStatus.nextBookingSourceType ===
      "organization-corporate-rate" &&
    !personalCreditSource;
  const membershipPauseStartsAt = bookingPrivileges?.membershipPauseStartsAt
    ? new Date(bookingPrivileges.membershipPauseStartsAt)
    : null;
  const formattedMaxBookingDate = maxBookingDate
    ? formatPhoenixDate(maxBookingDate)
    : null;
  const formattedRenewalCutoff = renewalCutoff
    ? formatPhoenixDate(renewalCutoff)
    : null;
  // CORP-DOUBLE-OCC-002 (Scenarios 1, 2, 3, 15) — affordance visibility
  // is driven by the server-rendered `eligibleAddOns` array (ADR-041 D3
  // Path A). The FE NEVER branches on `user.organizationCode`,
  // `familyMemberUserId`, or any client-stored field. Session-type +
  // date eligibility flows through the same array via the BE — see
  // `useEffect` on `eligibleAddOns` above.
  const showCorpDoubleOccupancyAffordance =
    sessionType === "infrared" &&
    eligibleAddOns.includes("corpDoubleOccupancy");
  // Surface B microcopy (Scenario 5 LOCKED) renders ONCE the guest has
  // typed into at least one of the three fields — avoids pre-input
  // flash on initial form open per Zuri's spec §Surface B.
  const corpDoubleOccMicrocopyVisible =
    useCorpDoubleOccupancy &&
    (corpGuestInfo.name.trim().length > 0 ||
      corpGuestInfo.email.trim().length > 0 ||
      corpGuestInfo.phone.trim().length > 0);
  // Dynamic name interpolation per spec — fallback to "your guest"
  // when the name field is empty.
  const corpDoubleOccMicrocopyName =
    corpGuestInfo.name.trim().length > 0
      ? corpGuestInfo.name.trim()
      : "your guest";
  const corpGuestFormValid = isCorpGuestFormValid(corpGuestInfo);
  const submitDisabled =
    submitInFlight ||
    (useCorpDoubleOccupancy && !corpGuestFormValid);
  /*console.log("Filtered Suites in render:", filteredSuites);*/
  return (
    <div className="book-session">
      <h2>Book a Session</h2>

      <div className="form-group">
        <label htmlFor="selectedDay">Select a Day:</label>
        <DatePicker
          id="selectedDay"
          selected={selectedDay}
          onChange={(date) => {
            setSelectedDay(date);
            setAppointmentDate(null);
            setSelectedSuite("");
          }}
          dateFormat="MMMM d, yyyy"
          minDate={new Date()}
          maxDate={maxBookingDate || undefined}
          placeholderText="Select a date - Available from today"
          dayClassName={(date) => {
            // Only badge past-renewal dates that are also WITHIN the booking
            // window. Dates past bookingWindowEnd are unreachable regardless
            // of renewal — badging them would imply clickability.
            if (!renewalCutoff || !isDateStrictlyAfter(date, renewalCutoff)) {
              return undefined;
            }
            if (maxBookingDate && date.getTime() > maxBookingDate.getTime()) {
              return undefined;
            }
            return "past-renewal-day";
          }}
        />
        {!isMassageBooking &&
          renewalCutoff &&
          selectedDay &&
          isDateStrictlyAfter(selectedDay, renewalCutoff) &&
          (hasNonMembershipCreditsForSession(userProfile, sessionType) ? (
            <div className="booking-window-info past-renewal-banner past-renewal-banner-info">
              This is past your {formattedRenewalCutoff} membership renewal —
              booking will use a drop-in or package credit (
              {countNonMembershipCreditsForSession(userProfile, sessionType)}{" "}
              remaining).
            </div>
          ) : (
            <div className="booking-window-info past-renewal-banner past-renewal-banner-warn">
              This is past your {formattedRenewalCutoff} membership renewal.
              You'll need to purchase a drop-in or package to book this date.
            </div>
          ))}
        {!isMassageBooking && maxBookingDate && (
          <div className="booking-window-info">
            {renewalLimitsBookingWindow ? (
              <>
                Your booking window is {bookingWindowDescription}. Your current
                membership renews on {formattedRenewalCutoff}. You can still
                browse availability through {formattedMaxBookingDate}, and
                membership-funded bookings after renewal will prompt you to
                purchase a session.
              </>
            ) : (
              <>
                Your booking window is {bookingWindowDescription}. You can
                currently book through {formattedMaxBookingDate}.
              </>
            )}
          </div>
        )}
        {!isMassageBooking &&
          membershipAccessStatus !== "active" &&
          membershipAccessMessage && (
          <div className="booking-window-info">
            {membershipAccessMessage}
            {membershipAccessStatus === "scheduled_pause" &&
              membershipPauseStartsAt && (
                <>
                  {" "}
                  Existing bookings before{" "}
                  {formatPhoenixDate(membershipPauseStartsAt)} will remain in
                  place.
                </>
              )}
          </div>
          )}
        {corporateProgramStatus?.enrolled &&
          !isMassageBooking &&
          corporateBookingMessage && (
          <div
            className={`booking-window-info booking-program-info ${
              corporateProgramNextRate > 0 && !personalCreditSource
                ? "booking-program-info-charged"
                : "booking-program-info-sponsored"
            }`}
          >
            <strong>{corporateProgramStatus.organizationCode} benefit:</strong>{" "}
            {corporateBookingMessage}
          </div>
          )}
        {isMassageBooking && (
          <div className="booking-window-info booking-program-info booking-program-info-sponsored">
            <strong>Massage booking:</strong> {getSessionDisplayName(sessionType)}{" "}
            {massagePrice
              ? `is $${massagePrice.toFixed(2)} + $${massageTax.toFixed(
                  2
                )} tax ($${massageTotal.toFixed(2)} total). `
              : ""}
            Cancellations made 2+ hours before your appointment receive a full refund,
            1-2 hours before receive a 50% refund, and less than 1 hour before receive no refund.
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="sessionType">Session Type:</label>
        <select
          id="sessionType"
          value={sessionType}
          onChange={(e) => {
            setSessionType(e.target.value);
            setAvailableSlots([]);
            setAvailableSuites([]);
            setAppointmentDate(null);
            setSelectedSuite("");
          }}
        >
          <option key="redlight" value="redlight">
            {getBookingOptionLabel("redlight")}
          </option>
          <option key="infrared" value="infrared">
            {getBookingOptionLabel("infrared")}
          </option>
          <option key="massage_60" value="massage_60">
            {getBookingOptionLabel("massage_60")}
          </option>
          <option key="massage_90" value="massage_90">
            {getBookingOptionLabel("massage_90")}
          </option>
        </select>
      </div>

      <div className="available-slots">
        <h3>
          Available Time Slots for {formatPhoenixDate(selectedDay)}{" "}
          <span className="slot-count">
            ({availableSlots.length} available)
          </span>
        </h3>
        <div className="slots-container">
          {availableSlots.length > 0 ? (
            availableSlots.map((slot, index) => (
              <button
                type="button"
                key={index}
                className={`slot-btn ${
                  appointmentDate &&
                  slot.getTime() === appointmentDate.getTime()
                    ? "selected"
                    : ""
                }`}
                onClick={() => setAppointmentDate(slot)}
              >
                {slot.toLocaleTimeString("en-US", {
                  timeZone: "America/Phoenix",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </button>
            ))
          ) : (
            <p>No available slots for this day.</p>
          )}
        </div>
      </div>
      {isMassageBooking ? (
        <div className="form-group">
          <p>{getSessionSuiteLabel(sessionType)}: Massage #1</p>
        </div>
      ) : filteredSuites.length > 0 ? (
        <div className="form-group">
          <label htmlFor="suiteSelection">
            Select {getSessionSuiteLabel(sessionType)}:
          </label>
          <select
            id="suiteSelection"
            value={selectedSuite}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedSuite(value);
              /*console.log("Selected Suite:", value);*/
            }}
            required
          >
            <option key="default-suite" value="">
              -- Select a Suite --
            </option>

            {/*console.log("Rendering suites:", filteredSuites)*/}
            {filteredSuites.map((suite) => {
              /*console.log("Rendering suite:", suite);*/
              return (
                <option
                  key={suite.id || suite.suiteNumber || suite.name}
                  value={suite.id || suite.suiteNumber}
                >
                  {suite.name}
                </option>
              );
            })}
          </select>
        </div>
      ) : (
        <div className="form-group">
          <p>No available suites for this session type on this day.</p>
        </div>
      )}

      {appointmentDate && (
        <p>
          Selected Time:{" "}
          {appointmentDate.toLocaleTimeString("en-US", {
            timeZone: "America/Phoenix",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </p>
      )}

      {sessionType === "infrared" && (
        <>
          {/*
            CORP-DOUBLE-OCC-002 Surfaces 1 + 2 + 3 — eligibility-gated
            corp-tier $15 affordance + inline guest form + Surface B
            acknowledged microcopy. Renders ONLY when the BE
            `eligibleAddOns` array contains "corpDoubleOccupancy" per
            ADR-041 D3 Path A (server-rendered). When this branch
            renders, the existing $25 `guest` add-on row is HIDDEN per
            Scenario 1 ("ONE option, not two competing prices").
          */}
          {showCorpDoubleOccupancyAffordance ? (
            <>
              <div
                className="form-group checkbox-group corp-double-occ-affordance"
                data-testid="corp-double-occ-affordance"
              >
                <label>
                  <input
                    type="checkbox"
                    checked={useCorpDoubleOccupancy}
                    disabled={submitInFlight}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setUseCorpDoubleOccupancy(isChecked);
                      // ADR-042 D1 disjoint: clear any in-flight $25
                      // guest state when the corp affordance is selected
                      // so a stale flag can't ride along on submit.
                      if (isChecked) {
                        setAddGuest(false);
                        setGuestInfo({ name: "", email: "", phone: "" });
                        setAgreeToPayForGuestPass(false);
                        setShowGuestPassConfirmation(false);
                      } else {
                        // Reset corp guest info + error on uncheck.
                        setCorpGuestInfo({ name: "", email: "", phone: "" });
                        setCorpDoubleOccError(null);
                      }
                    }}
                  />
                  <span className="corp-double-occ-label">
                    {corpDoubleOccPriceText
                      ? `Add a Guest (${corpDoubleOccPriceText})`
                      : "Add a Guest"}
                  </span>
                </label>
              </div>

              {useCorpDoubleOccupancy && (
                <div
                  className="guest-info-form corp-double-occ-form"
                  data-testid="corp-double-occ-guest-form"
                >
                  <h4>Guest Information</h4>
                  <p className="corp-double-occ-form-helper">
                    We'll need a few details so your guest can join the
                    session.
                  </p>
                  <div className="form-group">
                    <label htmlFor="corpGuestName">
                      Guest Name<span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="corpGuestName"
                      data-testid="corp-double-occ-guest-name"
                      value={corpGuestInfo.name}
                      onChange={(e) =>
                        setCorpGuestInfo({
                          ...corpGuestInfo,
                          name: e.target.value,
                        })
                      }
                      placeholder="Jane Smith"
                      required
                      disabled={submitInFlight}
                      // CORP-DOUBLE-OCC-002 polish (Nia D2/Surface) — when
                      // the affordance toggle is flipped on and this form
                      // expands, focus jumps to the Guest Name field so
                      // the corp member can start typing immediately.
                      // Safe to use because this input is conditionally
                      // mounted (parent renders only when
                      // `useCorpDoubleOccupancy === true`), so autoFocus
                      // fires exactly on the expand transition.
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="corpGuestEmail">
                      Guest Email<span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="email"
                      id="corpGuestEmail"
                      data-testid="corp-double-occ-guest-email"
                      value={corpGuestInfo.email}
                      autoComplete="off"
                      onChange={(e) =>
                        setCorpGuestInfo({
                          ...corpGuestInfo,
                          email: e.target.value,
                        })
                      }
                      placeholder="jane@email.com"
                      required
                      disabled={submitInFlight}
                    />
                    {corpGuestInfo.email.length > 0 &&
                      !isValidCorpGuestEmail(corpGuestInfo.email) && (
                        <p className="corp-double-occ-field-error">
                          Please enter a valid email address.
                        </p>
                      )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="corpGuestPhone">
                      Guest Phone<span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="tel"
                      id="corpGuestPhone"
                      data-testid="corp-double-occ-guest-phone"
                      value={corpGuestInfo.phone}
                      onChange={(e) =>
                        setCorpGuestInfo({
                          ...corpGuestInfo,
                          phone: e.target.value,
                        })
                      }
                      placeholder="(555) 123-4567"
                      required
                      disabled={submitInFlight}
                    />
                    {corpGuestInfo.phone.length > 0 &&
                      !isValidCorpGuestPhone(corpGuestInfo.phone) && (
                        <p className="corp-double-occ-field-error">
                          Please enter a valid US phone number.
                        </p>
                      )}
                  </div>
                  {corpDoubleOccMicrocopyVisible && (
                    <div
                      className="corp-double-occ-microcopy"
                      role="status"
                      data-testid="corp-double-occ-microcopy"
                    >
                      <p className="corp-double-occ-microcopy-primary">
                        We'll send {corpDoubleOccMicrocopyName} a welcome
                        email to claim their account.
                      </p>
                      <p className="corp-double-occ-microcopy-privacy">
                        Your guest will not be added to any marketing
                        communications.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {corpDoubleOccError && (
                <div
                  className="corp-double-occ-error"
                  role="alert"
                  data-testid="corp-double-occ-error"
                >
                  <h4 className="corp-double-occ-error-title">
                    {corpDoubleOccError.title}
                  </h4>
                  <p className="corp-double-occ-error-body">
                    {corpDoubleOccError.body}
                  </p>
                  <div className="corp-double-occ-error-actions">
                    {/* CORP-DOUBLE-OCC-002 polish (Nia D2 finding) —
                        Surface 6 "Try again" button. Clears the inline
                        error and re-invokes the booking submit with the
                        preserved guest form values (form values are
                        already preserved in `corpGuestInfo` state per
                        Scenarios 11 + 13 — this button just re-runs
                        the submit path against them). The standalone
                        "Update payment method" link remains for the
                        case where the user wants to update PM first. */}
                    <button
                      type="button"
                      className="corp-double-occ-error-retry"
                      data-testid="corp-double-occ-error-retry"
                      disabled={submitInFlight}
                      onClick={() => {
                        setCorpDoubleOccError(null);
                        handleSubmit({ preventDefault: () => {} });
                      }}
                    >
                      Try again
                    </button>
                    <Link
                      to="/payment"
                      className="corp-double-occ-error-link"
                    >
                      Update payment method
                    </Link>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={addGuest}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setAddGuest(isChecked);

                    if (!isChecked) {
                      // Reset guest info if unchecked
                      setGuestInfo({ name: "", email: "", phone: "" });
                      setAgreeToPayForGuestPass(false);
                      setShowGuestPassConfirmation(false);
                    } else {
                      if (!hasGuestPasses) {
                        setShowGuestPassConfirmation(true);
                      }
                    }
                  }}
                />
                Add a Guest
              </label>
            </div>
          )}

          {/* Legacy $25 guest path — confirmation modal + form. Hidden
              when the corp affordance is rendered per Scenario 1 (ONE
              option). When non-corp customers (Carol) are on this
              branch, the existing flow is byte-untouched (Scenario 3 +
              Scenario 14 explicit non-regression). */}
          {!showCorpDoubleOccupancyAffordance && showGuestPassConfirmation && (
            <div className="add-on-confirmation">
              <div className="confirmation-content">
                <h4>Guest Pass Required</h4>
                <p>
                  You don't have any guest passes available. Adding a guest will
                  cost {guestPriceText || "the guest fee"}
                  {pricing?.isWellnessWednesday ? " (Wellness Wednesday price)" : ""}{" "}
                  plus 8.6% AZ/Pima sales tax, which will be charged to your
                  saved card immediately when you complete this booking.
                </p>
                <div className="confirmation-actions">
                  <button
                    type="button"
                    className="confirm-btn"
                    onClick={() => {
                      setAgreeToPayForGuestPass(true);
                      setShowGuestPassConfirmation(false);
                    }}
                  >
                    {guestPriceText
                      ? `Agree to Pay ${guestPriceText}`
                      : "Agree to Pay"}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setAddGuest(false);
                      setShowGuestPassConfirmation(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {!showCorpDoubleOccupancyAffordance && addGuest && (
            <div className="guest-info-form">
              <h4>Guest Information</h4>
              <div className="form-group">
                <label htmlFor="guestName">Guest Name*</label>
                <input
                  type="text"
                  id="guestName"
                  value={guestInfo.name}
                  onChange={(e) =>
                    setGuestInfo({ ...guestInfo, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="guestEmail">Guest Email*</label>
                <input
                  type="email"
                  id="guestEmail"
                  value={guestInfo.email}
                  onChange={(e) =>
                    setGuestInfo({ ...guestInfo, email: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="guestPhone">Guest Phone*</label>
                <input
                  type="tel"
                  id="guestPhone"
                  value={guestInfo.phone}
                  onChange={(e) =>
                    setGuestInfo({ ...guestInfo, phone: e.target.value })
                  }
                  required
                />
              </div>
              <p className="guest-info-note">
                * Guest information is required for double occupancy bookings.
              </p>
            </div>
          )}
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={halotherapy}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setHalotherapy(isChecked);

                  if (!isChecked) {
                    setAgreeToPayForHalotherapy(false);
                    setShowHalotherapyConfirmation(false);
                  } else {
                    if (!hasHalotherapyEntitlement) {
                      setShowHalotherapyConfirmation(true);
                    }
                  }
                }}
              />
              Add Halo Therapy
            </label>
          </div>

          {showHalotherapyConfirmation && (
            <div className="add-on-confirmation">
              <div className="confirmation-content">
                <h4>Halotherapy Add-On Required</h4>
                <p>
                  You don't have any halotherapy sessions available. Adding
                  halotherapy will cost {halotherapyPriceText || "the halotherapy fee"}{" "}
                  plus 8.6% AZ/Pima sales tax, which will be charged to your
                  saved card immediately when you complete this booking.
                </p>
                <div className="confirmation-actions">
                  <button
                    type="button"
                    className="confirm-btn"
                    onClick={() => {
                      setAgreeToPayForHalotherapy(true);
                      setShowHalotherapyConfirmation(false);
                    }}
                  >
                    {halotherapyPriceText
                      ? `Agree to Pay ${halotherapyPriceText}`
                      : "Agree to Pay"}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setHalotherapy(false);
                      setShowHalotherapyConfirmation(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {inlineChargeItems.length > 0 && (
        <div className="booking-charge-summary">
          <h4>Booking Charges</h4>
          <div className="booking-charge-summary-label">
            Charged now at booking
          </div>
          {inlineChargeItems.map((item) => (
            <div key={item.label} className="booking-charge-summary-row">
              <span>{item.label}</span>
              <strong>
                {item.amountCents != null
                  ? `$${centsToDollars(item.amountCents).toFixed(2)}`
                  : "—"}
              </strong>
            </div>
          ))}
          <div className="booking-charge-summary-row">
            <span>AZ/Pima sales tax (8.6%)</span>
            <strong>
              {inlineChargePricesReady ? `$${inlineChargeTax.toFixed(2)}` : "—"}
            </strong>
          </div>
          <div className="booking-charge-summary-total">
            <span>Total charged now</span>
            <strong>
              {inlineChargePricesReady
                ? `$${totalInlineCharge.toFixed(2)}`
                : "—"}
            </strong>
          </div>
          <p className="booking-charge-summary-note">
            A saved card is required for inline charges at booking time.
          </p>
        </div>
      )}

      {isMassageBooking && massagePrice && (
        <div className="booking-charge-summary booking-charge-summary-massage">
          <h4>Massage Payment</h4>
          <p className="booking-charge-summary-note">
            Your saved card will be charged ${massageTotal.toFixed(2)} now
            (${massagePrice.toFixed(2)} + ${massageTax.toFixed(2)} AZ/Pima tax)
            to confirm this massage booking.
          </p>
        </div>
      )}

      <div className="terms-agreement">
        <div className="checkbox-container">
          <input
            type="checkbox"
            id="combined-checkbox"
            checked={termsAgreed}
            onChange={() => setTermsAgreed(!termsAgreed)}
          />
          <label htmlFor="combined-checkbox">
            I agree to the{" "}
            <Link to="/terms-of-service" target="_blank">
              Terms of Service
            </Link>
            {", "}
            and{" "}
            <Link to="/studio-policy" target="_blank">
              Studio Policy
            </Link>
            , and consent to receive booking-related email and SMS
            communications from Diviti Adora.
          </label>
        </div>
      </div>

      {/* CORP-FAMILY-008 (ADR-045 D1/D2 + A1/A2/A3 LOCKED 2026-06-15) —
          family-member corp-rate explicit consent dialog. Renders only when
          the user is a family member whose next booking will hit the
          corporate rate (A3 LOCK — employees skip this dialog). Mirrors the
          existing $25 retail guest-pass "Agree to Pay" consent pattern
          above. The BE writes `consentRecordedAt` server-side when the
          booking arrives with `consentGiven: true`. */}
      {showCorpRateConsent && isFamilyMemberCorpRateUser && (
        <div
          className="add-on-confirmation"
          data-testid="corp-rate-consent-dialog"
        >
          <div className="confirmation-content">
            <h4>Confirm Corporate Rate Booking</h4>
            <p>
              You'll be charged the{" "}
              {corporateProgramStatus?.organizationCode || "corporate"} rate to
              your card on file:
            </p>
            <ul className="corp-rate-consent-breakdown">
              <li>
                <span>Corp rate</span>
                <span data-testid="corp-rate-consent-base">
                  ${corpRateForConsent.toFixed(2)}
                </span>
              </li>
              <li>
                <span>Tax (8.6% AZ/Pima)</span>
                <span data-testid="corp-rate-consent-tax">
                  ${corpRateTax.toFixed(2)}
                </span>
              </li>
              <li className="corp-rate-consent-total-row">
                <span>
                  <strong>Total</strong>
                </span>
                <span data-testid="corp-rate-consent-total">
                  <strong>${corpRateTotal.toFixed(2)}</strong>
                </span>
              </li>
            </ul>
            <p className="corp-rate-consent-rationale">
              This rate is available because you're linked as a family member
              on a{" "}
              {corporateProgramStatus?.organizationCode || "corporate"}{" "}
              program.
            </p>
            <div className="confirmation-actions">
              <button
                type="button"
                className="confirm-btn"
                data-testid="corp-rate-consent-authorize"
                onClick={() => {
                  agreeToPayForCorpRateRef.current = true;
                  setAgreeToPayForCorpRate(true);
                  setShowCorpRateConsent(false);
                  handleSubmit({ preventDefault: () => {} });
                }}
              >
                Authorize ${corpRateTotal.toFixed(2)} Charge
              </button>
              <button
                type="button"
                className="cancel-btn"
                data-testid="corp-rate-consent-cancel"
                onClick={() => {
                  agreeToPayForCorpRateRef.current = false;
                  setShowCorpRateConsent(false);
                  setAgreeToPayForCorpRate(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="form-actions">
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={submitDisabled}
          data-testid="book-session-submit"
        >
          {submitInFlight
            ? "Booking…"
            : useCorpDoubleOccupancy && inlineChargePricesReady
            ? `Submit Booking · +$${totalInlineCharge.toFixed(2)}`
            : "Book Session"}
        </button>
        <button type="button" onClick={handleCancel} className="cancel-btn">
          Cancel
        </button>
      </div>

      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{error}</p>}

      {/* CORP-DOUBLE-OCC-002 Surface 4 — post-success confirmation
          block. Renders briefly between submit-success and the
          navigation to MyBookings (the existing 900ms window). The
          welcome-email acknowledgment renders only when the BE returned
          `created: true` (Scenario 10: existing-account guest gets NO
          acknowledgment). */}
      {corpDoubleOccSuccess && (
        <div
          className="corp-double-occ-confirmation"
          role="status"
          data-testid="corp-double-occ-confirmation"
        >
          <p className="corp-double-occ-confirmation-guest">
            <strong>Guest:</strong> {corpDoubleOccSuccess.guestName}
          </p>
          <p className="corp-double-occ-confirmation-lineitem">
            +1 Double Occupancy{" "}
            {pricing?.addOns?.corpDoubleOccupancy && (
              <span>
                $
                {centsToDollars(
                  pricing.addOns.corpDoubleOccupancy.amountCents
                ).toFixed(2)}
              </span>
            )}
          </p>
          {corpDoubleOccSuccess.welcomeEmailSent && (
            <div
              className="corp-double-occ-confirmation-welcome"
              data-testid="corp-double-occ-welcome-email-ack"
            >
              <strong>Welcome email sent.</strong> We've sent{" "}
              {corpDoubleOccSuccess.guestName} a welcome email at{" "}
              {corpDoubleOccSuccess.guestEmail} so they can set up their
              account before the session.
            </div>
          )}
        </div>
      )}

      {purchaseChoiceModal && (
        <div
          className="purchase-choice-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="purchase-choice-title"
          data-testid="purchase-choice-modal"
        >
          <div className="purchase-choice-modal">
            <h3 id="purchase-choice-title">
              {purchaseChoiceModal.corporateRate
                ? "Your membership has not renewed"
                : "Purchase required"}
            </h3>
            <p>
              {purchaseChoiceModal.corporateRate
                ? `Your membership doesn't cover this date. You can purchase a drop-in or package, or continue at your $${purchaseChoiceModal.corporateRate.amountDue} corporate rate — the charge will be applied now if you continue.`
                : "Your membership doesn't cover this date. Choose how you'd like to pay for this session — you'll return here to finish booking after checkout."}
            </p>
            <div className="purchase-choice-modal-actions">
              <button
                type="button"
                className="purchase-choice-btn purchase-choice-btn-primary"
                onClick={() => handlePurchaseChoice("/drop-in-sessions")}
              >
                Purchase drop-in
              </button>
              <button
                type="button"
                className="purchase-choice-btn purchase-choice-btn-primary"
                onClick={() => handlePurchaseChoice("/studio-packages")}
              >
                Purchase package
              </button>
              {purchaseChoiceModal.corporateRate && (
                <button
                  type="button"
                  className="purchase-choice-btn purchase-choice-btn-primary"
                  onClick={handleContinueAtCorporateRate}
                >
                  Continue at ${purchaseChoiceModal.corporateRate.amountDue} corporate rate
                </button>
              )}
              <button
                type="button"
                className="purchase-choice-btn purchase-choice-btn-cancel"
                onClick={() => setPurchaseChoiceModal(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookSession;
